import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CompanyFlags } from 'voxloud-types/dist/models';

import { AccountService } from '../components/shell/account.service';

export type FeatureFlagName = string;

const DEFAULT_FLAGS: Record<string, boolean> = {};
const SESSION_KEY = 'featureFlags';

@Injectable({ providedIn: 'root' })
export class FeatureToggleService {
  private flags: Record<string, boolean> = { ...DEFAULT_FLAGS };
  private loadedCompanyId: number | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly account: AccountService
  ) {}

  public loadFlags(companyId: number): Observable<void> {
    if (this.loadedCompanyId === companyId) return of(void 0);
    this.loadedCompanyId = companyId;
    this.flags = { ...DEFAULT_FLAGS };
    this.loadFromStorage(companyId);
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.account.current.token,
      'Voverc-Jwt-Auth': this.account.current.jwtToken,
    });

    return this.http
      .get<CompanyFlags>(`${environment.API_URL}/api/v1/feature-flags/companies/${companyId}?detail=false`, { headers })
      .pipe(
        tap((response) => {
          if (response.company_id !== companyId) {
            console.error('[FeatureToggleService] company mismatch in response');
            if (this.loadedCompanyId === companyId) {
              this.loadedCompanyId = null;
            }

            return;
          }
          this.mergeAndPersist(companyId, this.filterBooleanValues(response.flags ?? {}));
        }),
        map(() => void 0),
        catchError((err) => {
          console.error('[FeatureToggleService] Failed to load feature flags:', err);
          if (this.loadedCompanyId === companyId) {
            this.loadedCompanyId = null;
          }

          return of(void 0);
        })
      );
  }

  public isEnabled(flagName: FeatureFlagName): boolean {
    return !!this.flags[flagName];
  }

  public clearFlags(): void {
    if (this.loadedCompanyId !== null) {
      sessionStorage.removeItem(this.sessionKey(this.loadedCompanyId));
    }
    this.flags = { ...DEFAULT_FLAGS };
    this.loadedCompanyId = null;
  }

  private loadFromStorage(companyId: number): void {
    const stored = sessionStorage.getItem(this.sessionKey(companyId));
    if (stored) {
      this.flags = { ...DEFAULT_FLAGS, ...this.parseStoredFlags(stored) };
    }
  }

  private mergeAndPersist(companyId: number, newFlags: Record<string, boolean>): void {
    if (companyId !== this.loadedCompanyId) return;
    this.flags = { ...DEFAULT_FLAGS, ...newFlags };
    sessionStorage.setItem(this.sessionKey(companyId), JSON.stringify(this.flags));
  }

  private parseStoredFlags(value: string | null): Record<string, boolean> {
    try {
      const parsed = JSON.parse(value ?? '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

      return this.filterBooleanValues(parsed);
    } catch {
      return {};
    }
  }

  private filterBooleanValues(obj: Record<string, unknown>): Record<string, boolean> {
    return Object.entries(obj)
      .filter(([, v]) => typeof v === 'boolean')
      .reduce<Record<string, boolean>>((acc, [k, v]) => {
        acc[k] = v as boolean;

        return acc;
      }, {});
  }

  private sessionKey(companyId: number): string {
    return `${SESSION_KEY}-${companyId}`;
  }
}
